import {
  AlwaysStencilFunc,
  BackSide,
  DecrementWrapStencilOp,
  DoubleSide,
  FrontSide,
  IncrementWrapStencilOp,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  NotEqualStencilFunc,
  Plane,
  PlaneGeometry,
  Quaternion,
  ReplaceStencilOp,
  Scene,
  ShaderMaterial,
  Vector2,
  Vector3,
  type Box3,
  type Camera,
  type Object3D,
  type WebGLRenderer,
} from 'three';

import { computeCrossSectionPlaneQuadFromAabb } from './cross-section-utils';

/** v1 cap base fill (linear; matches prior MeshBasicMaterial cap). */
const CAP_BASE_HEX = 0x8a8a8a;
/** Darker stripe in linear RGB (Fusion-style single-direction hatch). */
const CAP_LINE_HEX = 0x3a3a3a;

/** Pixel pitch between parallel hatch lines (screen space). */
const STRIPE_PERIOD_PX = 14;
/** Full width of each dark stripe in pixels (antialiased at both edges). */
const LINE_WIDTH_PX = 1.85;
/** Hatch angle in radians (pixel-space diagonal). */
const HATCH_ANGLE_RAD = Math.PI / 4;

/** Full clipping-plane backdrop (linear RGB); semi-transparent over geometry. */
const PLANE_FILL_HEX = 0x6a8cae;
const PLANE_FILL_ALPHA = 0.2;
/** Screen-space border thickness in pixels (quad silhouette). */
const PLANE_BORDER_PX = 2.5;

function hexToLinearRgb(hex: number): Vector3 {
  const r = ((hex >> 16) & 255) / 255;
  const g = ((hex >> 8) & 255) / 255;
  const b = (hex & 255) / 255;
  return new Vector3(r, g, b);
}

const capVertexShader = /* glsl */ `
void main() {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const capFragmentShader = /* glsl */ `
uniform vec2 resolution;
uniform float stripePeriodPx;
uniform float lineWidthPx;
uniform vec3 baseColor;
uniform vec3 lineColor;
uniform float hatchCos;
uniform float hatchSin;

void main() {
  vec2 dir = vec2(hatchCos, hatchSin);
  float v = dot(gl_FragCoord.xy, dir);
  float period = stripePeriodPx;
  float phase = fract(v / period);
  // Symmetric distance to nearest stripe center (phase 0 == 1); avoids doubled lines / moiré at the fract wrap.
  float dist = min(phase, 1.0 - phase) * period;
  float halfW = lineWidthPx * 0.5;
  float aa = max(0.75, 0.35 * lineWidthPx);
  float lineAmt = 1.0 - smoothstep(halfW, halfW + aa, dist);
  gl_FragColor = vec4(mix(baseColor, lineColor, lineAmt), 1.0);
}
`;

const planeBackdropVertexShader = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const planeBackdropFragmentShader = /* glsl */ `
varying vec2 vUv;
uniform vec3 fillColor;
uniform float fillAlpha;
uniform float borderPx;

void main() {
  float d = min(min(vUv.x, 1.0 - vUv.x), min(vUv.y, 1.0 - vUv.y));
  vec2 g = vec2(dFdx(d), dFdy(d));
  float distPx = d / max(length(g), 1e-5);
  float borderAmt = 1.0 - smoothstep(0.0, borderPx, distPx);
  vec3 rgb = mix(fillColor, vec3(0.0), borderAmt);
  float alpha = mix(fillAlpha, 1.0, borderAmt);
  gl_FragColor = vec4(rgb, alpha);
}
`;

function createPlaneBackdropMaterial(): ShaderMaterial {
  const fillColor = hexToLinearRgb(PLANE_FILL_HEX);
  return new ShaderMaterial({
    uniforms: {
      fillColor: { value: fillColor },
      fillAlpha: { value: PLANE_FILL_ALPHA },
      borderPx: { value: PLANE_BORDER_PX },
    },
    vertexShader: planeBackdropVertexShader,
    fragmentShader: planeBackdropFragmentShader,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    side: DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
    clipping: false,
    toneMapped: false,
    stencilWrite: false,
  });
}

type MaterialStash = {
  obj: Mesh;
  material: Mesh['material'];
  renderOrder: number;
};

function createCapHatchMaterial(): ShaderMaterial {
  const baseColor = hexToLinearRgb(CAP_BASE_HEX);
  const lineColor = hexToLinearRgb(CAP_LINE_HEX);
  const mat = new ShaderMaterial({
    uniforms: {
      resolution: { value: new Vector2(1, 1) },
      stripePeriodPx: { value: STRIPE_PERIOD_PX },
      lineWidthPx: { value: LINE_WIDTH_PX },
      baseColor: { value: baseColor },
      lineColor: { value: lineColor },
      hatchCos: { value: Math.cos(HATCH_ANGLE_RAD) },
      hatchSin: { value: Math.sin(HATCH_ANGLE_RAD) },
    },
    vertexShader: capVertexShader,
    fragmentShader: capFragmentShader,
    depthWrite: false,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    clipping: false,
    toneMapped: false,
    stencilWrite: true,
    stencilRef: 0,
    stencilFunc: NotEqualStencilFunc,
    stencilFail: ReplaceStencilOp,
    stencilZFail: ReplaceStencilOp,
    stencilZPass: ReplaceStencilOp,
  });
  return mat;
}

/**
 * Stencil buildup + cap fill (three.js webgl_clipping_stencil pattern, single plane).
 * Only Mesh drawables participate; line/sprite geometry is skipped.
 */
export class CrossSectionStencilCapPass {
  private readonly stencilBackMat: MeshBasicMaterial;
  private readonly stencilFrontMat: MeshBasicMaterial;
  private readonly planeBackdropMat: ShaderMaterial;
  private readonly capMat: ShaderMaterial;
  private readonly planeGeom: PlaneGeometry;
  private readonly planeBackdropMesh: Mesh;
  private readonly capMesh: Mesh;
  private readonly planeBackdropScene = new Scene();
  private readonly capScene = new Scene();
  private readonly drawingBufferSize = new Vector2();
  private readonly planeQuadCenter = new Vector3();
  private readonly planeQuadU = new Vector3();
  private readonly planeQuadV = new Vector3();
  private readonly planeQuadVNeg = new Vector3();
  private readonly planeQuadZ = new Vector3();
  private readonly planeQuadBasis = new Matrix4();
  private readonly planeQuadQuat = new Quaternion();

  constructor(private readonly clippingPlane: Plane) {
    const makeStencilBase = () => {
      const m = new MeshBasicMaterial();
      m.depthWrite = false;
      m.depthTest = false;
      m.colorWrite = false;
      (m as MeshBasicMaterial & { clipping: boolean }).clipping = true;
      m.stencilWrite = true;
      m.stencilFunc = AlwaysStencilFunc;
      return m;
    };

    this.stencilBackMat = makeStencilBase();
    this.stencilBackMat.side = BackSide;
    this.stencilBackMat.clippingPlanes = [clippingPlane];
    this.stencilBackMat.stencilFail = IncrementWrapStencilOp;
    this.stencilBackMat.stencilZFail = IncrementWrapStencilOp;
    this.stencilBackMat.stencilZPass = IncrementWrapStencilOp;

    this.stencilFrontMat = makeStencilBase();
    this.stencilFrontMat.side = FrontSide;
    this.stencilFrontMat.clippingPlanes = [clippingPlane];
    this.stencilFrontMat.stencilFail = DecrementWrapStencilOp;
    this.stencilFrontMat.stencilZFail = DecrementWrapStencilOp;
    this.stencilFrontMat.stencilZPass = DecrementWrapStencilOp;

    this.planeBackdropMat = createPlaneBackdropMaterial();
    this.capMat = createCapHatchMaterial();

    this.planeGeom = new PlaneGeometry(1, 1);
    this.planeBackdropMesh = new Mesh(this.planeGeom, this.planeBackdropMat);
    this.planeBackdropMesh.frustumCulled = false;
    this.planeBackdropMesh.renderOrder = 0;
    this.planeBackdropScene.add(this.planeBackdropMesh);

    this.capMesh = new Mesh(this.planeGeom, this.capMat);
    this.capMesh.frustumCulled = false;
    this.capMesh.renderOrder = 1;
    this.capMesh.onAfterRender = (renderer: WebGLRenderer) => {
      renderer.clearStencil();
    };
    this.capScene.add(this.capMesh);
  }

  dispose(): void {
    this.stencilBackMat.dispose();
    this.stencilFrontMat.dispose();
    this.planeBackdropMat.dispose();
    this.capMat.dispose();
    this.planeGeom.dispose();
  }

  /**
   * Positions the plane backdrop and hatch quad on the clipping plane. With a valid AABB,
   * size follows the projected silhouette (depends on cut angle); otherwise a modest fallback.
   */
  updateCapMesh(plane: Plane, worldBounds: Box3 | undefined): void {
    const nx = plane.normal.x;
    const ny = plane.normal.y;
    const nz = plane.normal.z;

    if (!worldBounds || worldBounds.isEmpty()) {
      const fallback = 400;
      for (const mesh of [this.planeBackdropMesh, this.capMesh]) {
        mesh.scale.set(1, 1, 1);
        plane.coplanarPoint(mesh.position);
        mesh.lookAt(
          mesh.position.x - nx,
          mesh.position.y - ny,
          mesh.position.z - nz,
        );
        mesh.scale.set(fallback, fallback, 1);
      }
      return;
    }

    const { width, height } = computeCrossSectionPlaneQuadFromAabb(
      worldBounds,
      plane,
      this.planeQuadCenter,
      this.planeQuadU,
      this.planeQuadV,
    );
    this.planeQuadVNeg.copy(this.planeQuadV).negate();
    // PlaneGeometry's front face is +local Z. Previous lookAt(center - n) put local +Z ≈ -plane.normal;
    // use right-handed (u, -v, u×(-v)) so local +Z = -(u×v) matches that (avoids front-face cull hiding caps).
    this.planeQuadZ
      .crossVectors(this.planeQuadU, this.planeQuadVNeg)
      .normalize();
    this.planeQuadBasis.makeBasis(
      this.planeQuadU,
      this.planeQuadVNeg,
      this.planeQuadZ,
    );
    this.planeQuadQuat.setFromRotationMatrix(this.planeQuadBasis);
    for (const mesh of [this.planeBackdropMesh, this.capMesh]) {
      mesh.scale.set(1, 1, 1);
      mesh.position.copy(this.planeQuadCenter);
      mesh.quaternion.copy(this.planeQuadQuat);
      mesh.scale.set(width, height, 1);
    }
  }

  private collectMeshes(root: Object3D): Mesh[] {
    const meshes: Mesh[] = [];
    root.traverse((o) => {
      if (!o.visible) return;
      const m = o as Mesh;
      if (m.isMesh && m.geometry) meshes.push(m);
    });
    return meshes;
  }

  private applyStencilMaterial(
    meshes: Mesh[],
    mat: MeshBasicMaterial,
  ): MaterialStash[] {
    const stash: MaterialStash[] = [];
    for (const obj of meshes) {
      stash.push({
        obj,
        material: obj.material,
        renderOrder: obj.renderOrder,
      });
      if (Array.isArray(obj.material)) {
        const n = obj.material.length;
        obj.material = Array.from({ length: n }, () => mat);
      } else {
        obj.material = mat;
      }
      obj.renderOrder = -1;
    }
    return stash;
  }

  private restoreMaterials(stash: MaterialStash[]): void {
    for (const { obj, material, renderOrder } of stash) {
      obj.material = material;
      obj.renderOrder = renderOrder;
    }
  }

  /** Two clipped passes: stencil != 0 marks the cut region (see three.js clipping stencil example). */
  renderStencilBuild(
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
  ): void {
    this.stencilBackMat.clippingPlanes = [this.clippingPlane];
    this.stencilFrontMat.clippingPlanes = [this.clippingPlane];

    const meshes = this.collectMeshes(scene);
    if (meshes.length === 0) return;

    const stashB = this.applyStencilMaterial(meshes, this.stencilBackMat);
    renderer.render(scene, camera);
    this.restoreMaterials(stashB);

    const stashF = this.applyStencilMaterial(meshes, this.stencilFrontMat);
    renderer.render(scene, camera);
    this.restoreMaterials(stashF);
  }

  /** Call after the main shaded scene pass so depth tests occlude the cap correctly. */
  renderCap(
    renderer: WebGLRenderer,
    camera: Camera,
    worldBounds: Box3 | undefined,
  ): void {
    renderer.getDrawingBufferSize(this.drawingBufferSize);
    this.capMat.uniforms.resolution.value.copy(this.drawingBufferSize);

    this.updateCapMesh(this.clippingPlane, worldBounds);
    // Full-plane tint + border first; hatch pass draws on top (same transforms, tighter polygon offset).
    renderer.render(this.planeBackdropScene, camera);
    renderer.render(this.capScene, camera);
  }

  /**
   * Semi-transparent section plane only (no stencil, no hatch cap). Used while picking a face
   * so the cut is not applied to geometry until the user confirms.
   */
  renderPlaneBackdropOnly(
    renderer: WebGLRenderer,
    camera: Camera,
    plane: Plane,
    worldBounds: Box3 | undefined,
  ): void {
    this.updateCapMesh(plane, worldBounds);
    renderer.render(this.planeBackdropScene, camera);
  }
}
